<template>
  <ComponentState :fetch-status="getOptionStrategiesStatus">
    <template #loading>
      <TableSkeleton :row-number="10" :column-number="8" />
    </template>

    <template #error>
      <Error
        @retry="
          () => queryClient.invalidateQueries(OPTION_QUERY_KEYS.strategies)
        "
      />
    </template>

    <NTable
      v-if="optionStrategies.length"
      :data="optionStrategies"
      :sort-by="{
        prop: 'value',
        order: 'descending',
      }"
      :highlight-by="{
        prop: 'isin',
        value: activeIsin,
      }"
      :striped="false"
      cell-padding="small"
      :column-splitter="false"
      class="mb-2 rounded-none border-none"
      fixed-header
      hover-alignment="right"
      hover-offset="15%"
      :identifier="(row) => `${row.isin}-${row.strategyType}`"
      @click.stop
      @row-click="(row) => updateActiveIsin(row.isin)"
    >
      <template #hoveredElements="{ row }">
        <div class="flex items-center h-full gap-2">
          <NegarEditIcon
            size="20"
            class="mx-2"
            @click.stop="openEditStrategy(row)"
          />

          <NegarTrashLightIcon
            size="20"
            class="text-critical-primary ml-2 cursor-pointer"
            @click.stop="handleDeleteStrategy(row)"
          />
        </div>
      </template>

      <NTableColumn
        v-slot="{ row }"
        :label="t('option.openStrategies')"
        class="h-9"
        prop="strategyType"
        sortable
        align="text-right"
      >
        <NText variant="caption2" weight="medium">
          {{ STRATEGY_TYPE[row.strategyType]?.label }}
        </NText>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('option.contractQuantity')"
        class="h-9"
        prop="quantity"
        sortable
        align="text-right"
      >
        <NText variant="caption2" weight="medium">
          {{ row.quantity }}
        </NText>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('option.firstUnderluingAsset')"
        class="h-9"
        prop="firstLegInstrumentName"
        sortable
        align="text-right"
      >
        <NText variant="caption2" weight="medium">
          {{ row.firstLegInstrumentName }}
        </NText>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('option.firstUnderlyingAssetPrice')"
        class="h-9"
        prop="firstLegStrikePrice"
        sortable
        align="text-right"
      >
        <NumberFormatter #="{ number }" :value="row.firstLegStrikePrice">
          <NText
            :key="row.firstLegStrikePrice"
            variant="caption2"
            weight="medium"
            class="rounded-md px-2 py-0.5"
          >
            {{ number }}
          </NText>
        </NumberFormatter>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('option.secondUnderluingAsset')"
        class="h-9"
        prop="secondLegInstrumentName"
        sortable
        align="text-right"
      >
        <NText variant="caption2" weight="medium">
          {{ row.secondLegInstrumentName || '-' }}
        </NText>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('option.secondUnderlyingAssetPrice')"
        class="h-9"
        prop="secondLegStrikePrice"
        sortable
        align="text-right"
      >
        <NumberFormatter
          v-if="row.secondLegStrikePrice"
          #="{ number }"
          :value="row.secondLegStrikePrice"
        >
          <NText
            :key="row.secondLegStrikePrice"
            variant="caption2"
            weight="medium"
            class="rounded-md px-2 py-0.5"
          >
            {{ number }}
          </NText>
        </NumberFormatter>

        <NText
          v-else
          :key="row.secondLegStrikePrice"
          variant="caption2"
          weight="medium"
          class="rounded-md px-2 py-0.5"
        >
          -
        </NText>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('option.settlementTime')"
        prop="physicalSettlementDate"
        sortable
        align="text-right"
      >
        <span class="inline-flex items-center">
          <NumberFormatter
            #="{ number }"
            :value="
              Math.abs(calcSettlementDifferenceDays(row.physicalSettlementDate))
            "
          >
            <NText weight="bold" variant="caption2">
              {{ number }}
            </NText>
          </NumberFormatter>

          <NText weight="bold" variant="caption2" class="ms-1">
            {{ t('misc.day') }}
          </NText>
        </span>
      </NTableColumn>
    </NTable>

    <EmptyList
      v-else
      :title="t('option.strategiesTableEmptyListTitle')"
      title-class="text-center mt-3"
      wrapper-class="w-full"
      class="h-full flex flex-col justify-center items-center rounded-md my-3"
    >
      <template #image>
        <Image src="empty_box" alt="not_found" class="w-28" />
      </template>
    </EmptyList>
  </ComponentState>
</template>

<script setup lang="ts">
import {
  OPTION_QUERY_KEYS,
  STRATEGY_TYPE,
  useOptionStrategiesQuery,
} from '@core/option';
import { useI18n } from 'vue-i18n';
import { useQueryClient } from '@libs/use';
import { useOptionDialogs } from '@/modules/option';
import { getTimeDifference } from '@libs/utils';
import { useGetServerClock } from '@core/order';
import { useDialog, useToast } from '@negar/core';
import { useActiveSymbolIsin } from '@/modules/symbol';
import { useDeleteStrategyQuery } from '@core/option';
import { NegarTrashLightIcon, NegarEditIcon } from '@negar/icons';

import Error from '@/components/Error.vue';
import Image from '@/components/Image.vue';
import EmptyList from '@/components/EmptyList.vue';
import NumberFormatter from '@/components/Number.vue';
import TableSkeleton from '@/components/TableSkeleton.vue';
import ComponentState from '@/components/ComponentState.vue';

const queryClient = useQueryClient();

const toast = useToast();
const { t } = useI18n();
const { time } = useGetServerClock();
const { remove: removeDialog } = useDialog();
const { activeIsin, updateActiveIsin } = useActiveSymbolIsin();

const { getOptionStrategiesStatus, optionStrategies } =
  useOptionStrategiesQuery();

const { openDeleteStrategy, openEditStrategy } = useOptionDialogs();

const { deleteStrategy, isLoadingDeleteStrategy } = useDeleteStrategyQuery({
  async onSuccess() {
    toast.neutral({
      message: t('option.strategyMessage.delete', {
        settlementType: t('option.settlementRequest.cash'),
      }),
      duration: 3000,
    });

    queryClient.invalidateQueries(OPTION_QUERY_KEYS.strategies);

    removeDialog();
  },
  onReject(error) {
    const message = error.response?.data[0].message;

    if (message) {
      toast.negative({
        message,
        duration: 3000,
      });
    } else {
      toast.negative({
        message: t('option.strategyMessage.error'),
        duration: 3000,
      });
    }
  },
});

const handleDeleteStrategy = (row) => {
  openDeleteStrategy({
    isin: row.isin,
    loading: isLoadingDeleteStrategy.value,
    onDelete: deleteStrategy,
  });
};

const calcSettlementDifferenceDays = (date: string) =>
  getTimeDifference({
    startTime: new Date(date as string).getTime(),
    endTime: new Date(time.value).getTime(),
  }).differenceInDays;
</script>
